'use strict';

/**
 * @typedef {Object} ExternalLogger
 * @property {Function} info - Info logovanie
 * @property {Function} error - Chybové logovanie
 * @property {Function} warn - Varovanie
 * @property {Function} [debug] - Debug logovanie (voliteľné)
 */

/**
 * Logger pre @soitron/json-value-crypto.
 *
 * Podporuje:
 * - Farebný výstup v termináli (ANSI kódy)
 * - Verbose mód (podrobné logy len ak je zapnutý)
 * - Dry-run prefix v logoch
 * - Injektovanie externého loggera (napr. winston, pino, vlastný)
 *
 * Všetky metódy sú statické — Logger funguje ako singleton.
 * Triedy v projekte používajú Logger priamo bez inštanciovania.
 *
 * @class
 *
 * @example
 * // Základné použitie
 * const { Logger } = require('@soitron/json-value-crypto');
 * Logger.setVerbose(true);
 * Logger.info('[CryptoEngine]', 'Odvodzujem kľúč...');
 * Logger.verbose('[CryptoEngine]', 'scrypt N=32768, r=8, p=1');
 *
 * @example
 * // Injektovanie externého loggera
 * const winston = require('winston');
 * Logger.setLogger(winston.createLogger({ ... }));
 *
 * @example
 * // Dry-run mód
 * Logger.setDryRun(true);
 * Logger.info('[CLI]', 'Šifrujem...'); // → "⚡ [DRY-RUN] [CLI] Šifrujem..."
 */
class Logger {
  /**
   * Externý logger (winston, pino, vlastný...).
   * Ak je nastavený, všetky logy sa presmerujú naň.
   *
   * @private
   * @type {ExternalLogger|null}
   */
  static _external = null;

  /**
   * Verbose mód — ak true, verbose() logy sa zobrazujú.
   *
   * @private
   * @type {boolean}
   */
  static _verbose = false;

  /**
   * Dry-run mód — pridáva prefix do logov.
   *
   * @private
   * @type {boolean}
   */
  static _dryRun = false;

  /** @constant {string} ANSI kód — zelená (úspech, šifrovanie) */
  static GREEN = '\x1b[92m';

  /** @constant {string} ANSI kód — červená (chyba) */
  static RED = '\x1b[91m';

  /** @constant {string} ANSI kód — žltá (varovanie) */
  static YELLOW = '\x1b[93m';

  /** @constant {string} ANSI kód — modrá (verbose/debug) */
  static BLUE = '\x1b[94m';

  /** @constant {string} ANSI kód — šedá (informácia) */
  static GRAY = '\x1b[90m';

  /** @constant {string} ANSI kód — reset farby */
  static RESET = '\x1b[0m';

  /**
   * Nastaví externý logger.
   *
   * Externý logger musí implementovať minimálne metódy:
   * `info(prefix, message)`, `error(prefix, message)`, `warn(prefix, message)`.
   * Voliteľne aj `debug(prefix, message)` pre verbose logy.
   *
   * @static
   * @param {ExternalLogger|null} logger - Externý logger alebo null pre reset
   *
   * @example
   * // Winston
   * Logger.setLogger(winston.createLogger({ ... }));
   *
   * // Vlastný logger
   * Logger.setLogger({
   *   info: (prefix, msg) => myApp.log('INFO', prefix, msg),
   *   error: (prefix, msg) => myApp.log('ERROR', prefix, msg),
   *   warn: (prefix, msg) => myApp.log('WARN', prefix, msg),
   *   debug: (prefix, msg) => myApp.log('DEBUG', prefix, msg),
   * });
   *
   * // Reset na predvolený terminálový logger
   * Logger.setLogger(null);
   */
  static setLogger(logger) {
    Logger._external = logger;
  }

  /**
   * Zapne alebo vypne verbose mód.
   *
   * Ak je verbose zapnutý, volania Logger.verbose() sa zobrazia.
   * Ak je vypnutý, verbose logy sa ticho ignorujú.
   *
   * @static
   * @param {boolean} enabled - True pre zapnutie
   */
  static setVerbose(enabled) {
    Logger._verbose = !!enabled;
  }

  /**
   * Zapne alebo vypne dry-run mód.
   *
   * V dry-run móde sa ku každému logu pridá prefix "⚡ [DRY-RUN]".
   * Samotná logika dry-run (nezapisovať súbory) je na volajúcom kóde.
   *
   * @static
   * @param {boolean} enabled - True pre zapnutie
   */
  static setDryRun(enabled) {
    Logger._dryRun = !!enabled;
  }

  /**
   * Vráti aktuálny stav verbose módu.
   *
   * @static
   * @returns {boolean}
   */
  static isVerbose() {
    return Logger._verbose;
  }

  /**
   * Vráti aktuálny stav dry-run módu.
   *
   * @static
   * @returns {boolean}
   */
  static isDryRun() {
    return Logger._dryRun;
  }

  /**
   * Vytvorí prefix s dry-run indikátorom ak je aktívny.
   *
   * @private
   * @static
   * @param {string} prefix - Pôvodný prefix
   * @returns {string} Prefix s voliteľným dry-run indikátorom
   */
  static _withDryRun(prefix) {
    return Logger._dryRun ? `⚡ [DRY-RUN] ${prefix}` : prefix;
  }

  /**
   * Vypíše informačnú správu.
   * Zobrazuje sa vždy (bez ohľadu na verbose).
   *
   * @static
   * @param {string} prefix - Prefix správy (napr. "[CryptoEngine]", "[CLI]")
   * @param {string} message - Text správy
   * @param {string} [color=''] - ANSI farba (ignoruje sa ak je externý logger)
   */
  static info(prefix, message, color = '') {
    const fullPrefix = Logger._withDryRun(prefix);
    if (Logger._external) {
      Logger._external.info(fullPrefix, message);
      return;
    }
    const reset = color ? Logger.RESET : '';
    console.log(`${color}${fullPrefix} ${message}${reset}`);
  }

  /**
   * Vypíše verbose správu.
   * Zobrazuje sa LEN ak je verbose mód zapnutý.
   *
   * Pri externom loggeri sa presmeruje na debug() ak existuje,
   * inak na info().
   *
   * @static
   * @param {string} prefix - Prefix správy
   * @param {string} message - Text správy
   */
  static verbose(prefix, message) {
    if (!Logger._verbose) return;
    const fullPrefix = Logger._withDryRun(prefix);
    if (Logger._external) {
      const fn = Logger._external.debug || Logger._external.info;
      fn.call(Logger._external, fullPrefix, message);
      return;
    }
    console.log(`${Logger.GRAY}${fullPrefix} ${message}${Logger.RESET}`);
  }

  /**
   * Vypíše úspešnú správu (zelená farba).
   * Zobrazuje sa vždy.
   *
   * @static
   * @param {string} prefix - Prefix správy
   * @param {string} message - Text správy
   */
  static success(prefix, message) {
    Logger.info(prefix, message, Logger.GREEN);
  }

  /**
   * Vypíše chybovú správu.
   * Zobrazuje sa vždy.
   *
   * @static
   * @param {string} prefix - Prefix správy
   * @param {string} message - Text chyby
   */
  static error(prefix, message) {
    const fullPrefix = Logger._withDryRun(prefix);
    if (Logger._external) {
      Logger._external.error(fullPrefix, message);
      return;
    }
    console.error(`${Logger.RED}${fullPrefix} ${message}${Logger.RESET}`);
  }

  /**
   * Vypíše varovanie.
   * Zobrazuje sa vždy.
   *
   * @static
   * @param {string} prefix - Prefix správy
   * @param {string} message - Text varovania
   */
  static warn(prefix, message) {
    const fullPrefix = Logger._withDryRun(prefix);
    if (Logger._external) {
      Logger._external.warn(fullPrefix, message);
      return;
    }
    console.warn(`${Logger.YELLOW}${fullPrefix} ${message}${Logger.RESET}`);
  }

  /**
   * Resetuje Logger do predvoleného stavu.
   * Odstráni externý logger, vypne verbose a dry-run.
   *
   * @static
   */
  static reset() {
    Logger._external = null;
    Logger._verbose = false;
    Logger._dryRun = false;
  }
}

module.exports = Logger;
